import { useState } from 'react';

import { Callout, EmailInput, Field, Loading, Panel, PasswordInput, PASSWORD_MIN_LENGTH, TextInput, explainFailure, kitText, useLoad } from '@affdice/portal-kit';

import type { AffiliateApi } from './client.ts';
import { t } from './messages.ts';

/**
 * An affiliate applying to a programme — §6.15.
 *
 * The endpoint existed and no page did, and the endpoint took a raw operator
 * ULID, which no programme can print on a link. So "affiliate signup" was
 * simultaneously built and unreachable.
 *
 * Two things this page must be careful about, and both are the kind that get
 * left out because they make the form feel less welcoming:
 *
 *   §6.5 — applying is not joining. The operator reviews it, and the page says
 *   so before the button rather than after. Somebody who believes they have
 *   joined and then hears nothing for a week reads the silence as a rejection.
 *
 *   §6.15b — "The affiliate MUST be shown, at acceptance, exactly what the new
 *   operator will and will not see… Consent that does not state its scope is
 *   not consent." The same list the invitation page shows, because the consent
 *   is the same consent.
 */
export function SignUpPage({ api, code }: { api: AffiliateApi; code: string }) {
  const programme = useLoad(() => api.programme(code), [code]);
  // §8.9b — a recruiter's referral code, from `?ref=`. Read once: the value
  // does not change while this page is open, and the response to applying is
  // identical either way (§6.15) — this is the only thing on the page that
  // differs.
  const [ref] = useState(() => new URLSearchParams(location.search).get('ref'));

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  if (programme.state === 'loading') return <div className="main"><Loading rows={3} /></div>;
  if (programme.state === 'error') {
    return (
      <div className="main signin">
        <div className="page-head"><h1>{t('apply.loadFailed')}</h1></div>
        <Panel title={t('apply.loadFailed.panel')}>
          <Callout tone="error">{explainFailure(programme.error).message}</Callout>
        </Panel>
      </div>
    );
  }

  // §6.15 — an unknown code and a programme taking no applications say the same
  // thing, because the difference is what somebody trying codes wants to learn.
  if (!programme.value.open) {
    return (
      <div className="main signin">
        <div className="page-head"><h1>{t('apply.closed')}</h1></div>
        <Panel title={t('apply.closed.panel')}>
          <Callout tone="caution">{programme.value.message}</Callout>
        </Panel>
      </div>
    );
  }

  const operator = programme.value.operator_trading_name ?? t('apply.theseBrands');

  if (done) {
    return (
      <div className="main signin">
        <div className="page-head"><h1>{t('apply.sent')}</h1></div>
        <Panel title={t('apply.sent.panel')}>
          <Callout>{done}</Callout>
        </Panel>
      </div>
    );
  }

  return (
    <div className="main signin">
      <div className="page-head">
        <h1>{t('apply.title', { operator })}</h1>
      </div>

      {/* §8.9b — a small note, never a claim about who sent the link: the
          server resolves the code (or ignores it) and says nothing either
          way (§6.15). */}
      {ref ? <Callout>{t('apply.referral.note')}</Callout> : null}

      {/* §6.15b — before the form, not after it. Consent that does not state
          its scope is not consent. */}
      <Panel title={t('apply.scope')}>
        <ul className="scope">
          <li>{t('apply.scope.contact')}</li>
          <li>{t('apply.scope.traffic', { operator })}</li>
        </ul>
        <ul className="scope will-not">
          <li>{t('apply.scope.notOthers')}</li>
          <li>{t('apply.scope.notHistory')}</li>
        </ul>
      </Panel>

      <Panel title={t('apply.panel')}>
        <div className="form">
          {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}

          <div className="form-row">
            <Field label={t('invite.field.name')}>
              <TextInput value={name} onChange={setName} placeholder="Northline Media" />
            </Field>
            <Field label={t('invite.field.country')} hint={t('invite.field.country.hint')}>
              <TextInput value={country} onChange={setCountry} placeholder="GB" />
            </Field>
          </div>

          <Field label={kitText('field.email')} hint={t('apply.field.email.hint')}>
            <EmailInput value={email} onChange={setEmail} placeholder="you@example.com" />
          </Field>

          <Field label={t('apply.field.phone')} hint={t('apply.field.phone.hint')}>
            <TextInput value={phone} onChange={setPhone} placeholder="+44 7700 900000" />
          </Field>

          <Field
            label={kitText('password.title')}
            hint={t('invite.field.password.hint')}
          >
            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
            />
          </Field>

          <div className="form-actions">
            <button
              className="primary"
              disabled={
                busy || name.trim() === '' || email.trim() === ''
                || country.trim().length !== 2 || password.length < PASSWORD_MIN_LENGTH
              }
              onClick={() => {
                setBusy(true);
                setFailure(null);
                api
                  .signUp({
                    code,
                    email,
                    display_name: name,
                    country: country.toUpperCase(),
                    password,
                    via: 'SELF_SIGNUP',
                    ...(phone.trim() ? { phone: phone.trim() } : {}),
                    ...(ref ? { ref } : {}),
                  })
                  .then((r) => setDone(r.message ?? t('apply.sent.fallback')))
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? t('apply.busy') : t('apply.submit')}
            </button>
          </div>
        </div>
      </Panel>

      {/* §6.5 — said before the button is pressed. Applying is not joining, and
          somebody who thinks it is reads the review period as a rejection. */}
      <Callout tone="caution">{t('apply.notAcceptance', { operator })}</Callout>
    </div>
  );
}
